// src/pages/Login.js
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert, 
  Paper,
  Fade
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';

const Login = () => {
  const { signInWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError('');
      await signInWithGoogle();
      navigate('/');
    } catch (error) {
      setError('فشل تسجيل الدخول. الرجاء المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  if (user) {
    navigate('/');
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: '#f8f9fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 3,
        px: 1
      }}
    >
      <Container maxWidth="xs">
        <Fade in timeout={600}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: '#fff',
              border: '1px solid #e0e0e0'
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                direction: 'rtl'
              }}
            >
              {/* Logo and Brand */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  mb: 2
                }}
              >
                <Box
                  component="img"
                  src="/logo192.png"
                  alt="A to Z Store Logo"
                  sx={{ 
                    height: 60, 
                    width: 60,
                    mb: 1.5
                  }}
                />
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 'bold',
                    mb: 1
                  }}
                >
                  <Box component="span" sx={{ color: '#0f3662' }}>A to </Box>
                  <Box component="span" sx={{ color: '#a72c2a' }}>Z</Box>
                  <Box component="span" sx={{ color: '#0f3662' }}> store</Box>
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  مرحباً بك في متجرنا للأزياء
                </Typography>
              </Box>

              {error && (
                <Fade in>
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3, 
                      width: '100%',
                      borderRadius: 2
                    }}
                  >
                    {error}
                  </Alert>
                </Fade>
              )}

              <Button
                variant="contained"
                endIcon={loading ? null : <GoogleIcon sx={{ mr: 1 }} />}
                onClick={handleGoogleLogin}
                disabled={loading}
                fullWidth
                sx={{
                  height: 50,
                  borderRadius: 2,
                  backgroundColor: '#0f3662',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  mb: 2,
                  '&:hover': {
                    backgroundColor: '#0d2d52',
                  },
                  '&:disabled': {
                    backgroundColor: '#0f366280',
                    color: '#ffffff80'
                  }
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'تسجيل الدخول باستخدام جوجل'
                )}
              </Button>

              {/* Features */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 3,
                  mb: 2,
                  flexWrap: 'wrap'
                }}
              >
                {['أزياء رجالية', 'أزياء نسائية', 'أزياء أطفال'].map((item, index) => (
                  <Typography
                    key={index}
                    variant="caption"
                    sx={{
                      color: '#0f3662',
                      fontWeight: 'bold',
                      px: 2,
                      py: 0.5,
                      backgroundColor: '#f0f4f8',
                      borderRadius: 1,
                      border: '1px solid #e0e8f0'
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>

              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  textAlign: 'center',
                  lineHeight: 1.5
                }}
              >
                بالاستمرار، أنت توافق على{' '}
                <Typography 
                  component="span" 
                  sx={{ 
                    color: '#0f3662',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}
                >
                  شروط الخدمة
                </Typography>
                {' '}و{' '}
                <Typography 
                  component="span" 
                  sx={{ 
                    color: '#0f3662',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  }}
                >
                  سياسة الخصوصية
                </Typography>
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default Login;
